import React, { CSSProperties, FC, ReactNode } from "react";
import { Skeleton as MuiSkeleton } from "@mui/material";
import { styled } from "@mui/system";
import { COLORS, FONTS, FONT_WEIGHTS, RADII, SPACINGS } from "../../common/consts";

/**
 * The skeleton vocabulary. Every loading state in the dashboard is drawn from
 * these so a page's placeholder has the same geometry as the page itself:
 * same card, same rows, same column widths, same row heights. The ink is the
 * secondary surface, which follows the theme, so the bones stay right in dark
 * mode too.
 */

export const Bone = styled(MuiSkeleton)`
  background-color: var(--color-backgroundSecondary, ${COLORS.backgroundSecondary});
  flex: 0 0 auto;
`;

interface BoneProps {
  width?: number | string;
  height?: number | string;
  style?: CSSProperties;
  className?: string;
}

/** A line of text, sized off the font size it stands in for. */
export const TextBone: FC<BoneProps & { size?: number | string }> = ({
  width = "100%",
  size = "0.875rem",
  style,
  className,
}) => (
  <Bone
    variant="text"
    width={width}
    className={className}
    style={{ fontSize: size, ...style }}
  />
);

/** A control: an input, a select, a button, a card. */
export const BlockBone: FC<BoneProps & { radius?: string }> = ({
  width = "100%",
  height = 40,
  radius = RADII.md,
  style,
  className,
}) => (
  <Bone
    variant="rounded"
    width={width}
    height={height}
    className={className}
    style={{ borderRadius: radius, ...style }}
  />
);

/** A status pill or a toggle, both of which are fully rounded. */
export const PillBone: FC<BoneProps> = ({ width = 48, height = 24, style, className }) => (
  <Bone
    variant="rounded"
    width={width}
    height={height}
    className={className}
    style={{ borderRadius: RADII.full, ...style }}
  />
);

export const CircleBone: FC<{ size?: number | string; style?: CSSProperties }> = ({
  size = 32,
  style,
}) => <Bone variant="circular" width={size} height={size} style={style} />;

const StatWrap = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS.xs};
  min-width: 6.5rem;
`;

/** The number-over-label pair the Insights stat cards are built from. */
export const StatBones: FC<{ valueWidth?: number; labelWidth?: number }> = ({
  valueWidth = 72,
  labelWidth = 96,
}) => (
  <StatWrap>
    <Bone variant="rounded" width={valueWidth} height={24} style={{ borderRadius: RADII.sm }} />
    <TextBone width={labelWidth} size="0.875rem" />
  </StatWrap>
);

/** Stands in for a single 1.5rem stat number, keeping the card's own layout. */
export const NumberBone: FC<{ width?: number }> = ({ width = 72 }) => (
  <Bone
    variant="rounded"
    width={width}
    height={24}
    style={{ borderRadius: RADII.sm, display: "inline-block", verticalAlign: "middle" }}
  />
);

export interface SkeletonColumn {
  /** Fixed column width in pixels, as the grid's own column has. */
  width?: number;
  /** Share of the leftover space, as the grid's own column has. */
  flex?: number;
  minWidth?: number;
  headerName?: ReactNode;
  /** Narrow, centred columns (toggles, checkboxes) get a centred bone. */
  align?: "left" | "center";
}

const TableWrap = styled("div")`
  width: 100%;
  box-sizing: border-box;
`;

const HeaderRow = styled("div")`
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--color-borderDark, ${COLORS.borderDark});
`;

const HeaderCell = styled("div")`
  font: ${FONTS.sm};
  font-weight: ${FONT_WEIGHTS.semibold};
  font-size: 0.813rem;
  color: var(--color-foregroundPrimary, var(--color-foregroundBrandTable, ${COLORS.foregroundBrandTable}));
  padding: 8px 0 16px 0;
  align-self: flex-end;
  box-sizing: border-box;
  overflow: hidden;
`;

const BodyRow = styled("div")`
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--color-borderDark, ${COLORS.borderDark});
`;

const BodyCell = styled("div")`
  display: flex;
  align-items: center;
  padding: 16px 0;
  box-sizing: border-box;
  overflow: hidden;
`;

/* The bones do not all run to the same length, or the table reads as a block
   of stripes. Fixed pattern, not random, so the widths hold still between
   renders. */
const CELL_WIDTHS = ["78%", "56%", "88%", "46%", "68%"];

const cellFlex = (column: SkeletonColumn): CSSProperties =>
  column.width != null
    ? { flex: `0 0 ${column.width}px`, width: column.width }
    : { flex: `${column.flex ?? 1} 1 0`, minWidth: column.minWidth ?? 0 };

/**
 * A table as the DataGrid draws it while its rows are still coming: the real
 * column headers over placeholder rows at the grid's own row height, with the
 * same 1px rule under each row.
 */
export const TableSkeleton: FC<{
  columns: SkeletonColumn[];
  rows?: number;
  rowHeight?: number;
  headerHeight?: number;
}> = ({ columns, rows = 6, rowHeight = 72, headerHeight = 56 }) => (
  <TableWrap>
    <HeaderRow style={{ height: headerHeight }}>
      {columns.map((column, i) => (
        <HeaderCell
          key={i}
          style={{
            ...cellFlex(column),
            textAlign: column.align === "center" ? "center" : "left",
          }}
        >
          {column.headerName}
        </HeaderCell>
      ))}
    </HeaderRow>
    {Array.from({ length: rows }, (_, row) => (
      <BodyRow key={row} style={{ height: rowHeight }}>
        {columns.map((column, i) => (
          <BodyCell
            key={i}
            style={{
              ...cellFlex(column),
              justifyContent: column.align === "center" ? "center" : "flex-start",
            }}
          >
            {column.align === "center" ? (
              <PillBone width={40} height={20} />
            ) : (
              <TextBone width={CELL_WIDTHS[(row + i) % CELL_WIDTHS.length]} />
            )}
          </BodyCell>
        ))}
      </BodyRow>
    ))}
  </TableWrap>
);

const ChartWrap = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${SPACINGS.md};
`;

const LegendRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACINGS.xl};
`;

const LegendItem = styled("div")`
  display: flex;
  align-items: center;
  gap: ${SPACINGS.sm};
`;

const Plot = styled("div")`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
`;

/* The chart's own y axis is 52px wide with the axis line drawn down its
   right edge; the ticks sit against it. */
const YAxisColumn = styled("div")`
  width: 52px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0.25rem 0.5rem 0.25rem 0;
  border-right: 1px solid var(--color-chartAxisLine, ${COLORS.chartAxisLine});
  box-sizing: border-box;
`;

const PlotArea = styled("div")`
  flex: 1 1 auto;
  display: flex;
  align-items: stretch;
  padding: 0.25rem 1.5rem 0 1.25rem;
  border-bottom: 1px solid var(--color-chartAxisLine, ${COLORS.chartAxisLine});
  box-sizing: border-box;
`;

const XAxisRow = styled("div")`
  display: flex;
  justify-content: space-between;
  padding-left: calc(52px + 1.25rem);
  padding-right: 1.5rem;
`;

/**
 * The line chart while its series are still loading: the legend above, the
 * y axis at its own 52px width, the plot area between the axis lines, and the
 * date ticks underneath — the same frame the chart lands in.
 */
export const ChartSkeleton: FC<{
  height?: number | string;
  series?: number;
  ticks?: number;
}> = ({ height = 300, series = 3, ticks = 6 }) => (
  <ChartWrap style={{ height, minHeight: height }}>
    <LegendRow>
      {Array.from({ length: series }, (_, i) => (
        <LegendItem key={i}>
          <CircleBone size={10} />
          <TextBone width={64} size="0.875rem" />
        </LegendItem>
      ))}
    </LegendRow>
    <Plot>
      <YAxisColumn>
        {Array.from({ length: 5 }, (_, i) => (
          <TextBone key={i} width={24} size="0.875rem" />
        ))}
      </YAxisColumn>
      <PlotArea>
        <Bone
          variant="rounded"
          width="100%"
          height="100%"
          style={{ borderRadius: RADII.md, opacity: 0.6 }}
        />
      </PlotArea>
    </Plot>
    <XAxisRow>
      {Array.from({ length: ticks }, (_, i) => (
        <TextBone key={i} width={44} size="0.875rem" />
      ))}
    </XAxisRow>
  </ChartWrap>
);
